import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { BellRing, BellOff, Crown, Heart, Megaphone, Siren, Ticket } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import SignalCard from "../components/SignalCard";
import Markets from "./Markets";
import { MessageCard } from "../components/MessageCard";
import { MarketAlertCard } from "../components/MarketAlertCard";
import { BiasCard } from "../components/BiasCard";
import { BiasSignalsModal } from "../components/BiasSignalsModal";
import { StrategyInsightsModal } from "../components/StrategyInsightsModal";
import { ClosedSignalsModal } from "../components/ClosedSignalsModal";
import { SignalsListModal } from "../components/SignalsListModal";
import { TradeSearch } from "../components/TradeSearch";
import WinRateDonut from "../components/WinRateDonut";
import { LotteryCountdown } from "../components/LotteryCountdown";
import { pushSupported, getSubscriptionState, subscribePush, unsubscribePush } from "../lib/push";
import { logError } from "../lib/logger";

const VIEWS = [
  { id: "all", label: "Tutti" },
  { id: "stoch", label: "A Mercato · Stoch Trend" },
  { id: "liquidity", label: "Pendenti · Liquidity Pool" },
  { id: "flg", label: "Fair Liquidity" },
  { id: "messages", label: "Messaggi", icon: Megaphone },
  { id: "alerts", label: "Avvisi Mercato", icon: Siren },
  { id: "favorites", label: "Preferiti", icon: Heart },
];

const STRAT_META = {
  "LIQUIDITY POOL": { label: "Liquidity Pool", color: "#3B82F6", testid: "liquidity-pool" },
  "STOCH TREND": { label: "Stoch Trend", color: "#F59E0B", testid: "stoch-trend" },
  "FAIR LIQUIDITY GAP": { label: "Fair Liquidity", color: "#14B8A6", testid: "fair-liquidity-gap" },
  "FLG SCALP": { label: "FLG Scalp", color: "#A855F7", testid: "flg-scalp" },
  "FLG RECOVERY": { label: "FLG Recovery", color: "#9CA3AF", testid: "flg-recovery" },
};

const STATUSES = [
  { id: "all", label: "Tutti", color: "text-white" },
  { id: "active", label: "Attivo", color: "text-blue-400" },
  { id: "breakeven", label: "SL a BE", color: "text-amber-300" },
  { id: "pending", label: "Pendente", color: "text-zinc-200" },
  { id: "tp_hit", label: "TP", color: "text-emerald-400" },
  { id: "sl_hit", label: "SL", color: "text-red-400" },
  { id: "cancelled", label: "Cancellati", color: "text-orange-400" },
];

const isPending = (s) => s.status === "active" && s.order_type === "LIMIT" && !s.filled;

const ASSETS = [
  { id: "all", label: "Tutte" },
  { id: "gold", label: "Oro (XAU)" },
  { id: "crypto", label: "Crypto" },
  { id: "forex", label: "Forex" },
];

const PERIODS = [
  { id: "all", label: "Sempre" },
  { id: "day", label: "Giornata", days: 1 },
  { id: "week", label: "Ultima settimana", days: 7 },
  { id: "month", label: "Ultimo mese", days: 30 },
];

function StatBox({ label, value, color, testid, onClick }) {
  const clickable = typeof onClick === "function";
  return (
    <div
      data-testid={testid}
      onClick={onClick}
      className={`bg-[#141414] border border-white/10 rounded-md p-4 ${clickable ? "cursor-pointer hover:border-white/30 hover:bg-[#181818] transition-colors" : ""}`}
    >
      <div className="text-xs text-zinc-500 uppercase tracking-wider">{label}</div>
      <div className={`font-mono text-2xl font-semibold mt-1 ${color || "text-white"}`}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [topTab, setTopTab] = useState("segnali");
  const [view, setView] = useState("all");
  const [status, setStatus] = useState("all");
  const [asset, setAsset] = useState("all");
  const [period, setPeriod] = useState("all");
  const [signals, setSignals] = useState([]);
  const [messages, setMessages] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [biases, setBiases] = useState([]);
  const [biasSel, setBiasSel] = useState(null);
  const [insights, setInsights] = useState(null);
  const [lottery, setLottery] = useState(null);
  const [insightMode, setInsightMode] = useState(null);
  const [closedOpen, setClosedOpen] = useState(false);
  const [activeOpen, setActiveOpen] = useState(false);
  const [tpOpen, setTpOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const sigReq = view === "favorites" ? api.get("/favorites") : api.get("/signals");
      const [sig, st, msg, bias, alertRes] = await Promise.all([sigReq, api.get("/stats"), api.get("/broadcasts"), api.get("/bias"), api.get("/market-alerts")]);
      setSignals(sig.data);
      setStats(st.data);
      setMessages(msg.data);
      setBiases(bias.data || []);
      setAlerts(alertRes.data || []);
    } catch (e) { logError("Errore caricamento dashboard:", e); } finally { if (!silent) setLoading(false); }
  }, [view]);

  const deleteMessage = async (id) => {
    if (!window.confirm("Eliminare questo messaggio per tutti?")) return;
    try {
      await api.delete(`/broadcasts/${id}`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success("Messaggio eliminato");
    } catch (e) {
      toast.error("Impossibile eliminare il messaggio");
    }
  };

  const deleteBias = async (id) => {
    if (!window.confirm("Eliminare questa GENERAL BIAS per tutti?")) return;
    try {
      await api.delete(`/bias/${id}`);
      setBiases((prev) => prev.filter((b) => b.id !== id));
      toast.success("Bias eliminata");
    } catch (e) {
      toast.error("Impossibile eliminare la bias");
    }
  };

  const deleteAlert = async (id) => {
    if (!window.confirm("Eliminare questo avviso per tutti?")) return;
    try {
      await api.delete(`/market-alerts/${id}`);
      setAlerts((prev) => prev.filter((a) => a.id !== id));
      toast.success("Avviso eliminato");
    } catch (e) {
      toast.error("Impossibile eliminare l'avviso");
    }
  };

  const toggleFavorite = async (id, isFav) => {
    setSignals((prev) => prev.map((x) => (x.id === id ? { ...x, is_favorite: !isFav } : x)));
    try {
      if (isFav) await api.delete(`/favorites/${id}`);
      else await api.post(`/favorites/${id}`);
      if (view === "favorites" && isFav) setSignals((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      setSignals((prev) => prev.map((x) => (x.id === id ? { ...x, is_favorite: isFav } : x)));
      toast.error("Impossibile aggiornare i preferiti");
    }
  };

  const byStrategy = useMemo(() => {
    let list = signals;
    if (view === "stoch") list = list.filter((s) => s.strategy_label === "STOCH TREND");
    else if (view === "liquidity") list = list.filter((s) => s.strategy_label === "LIQUIDITY POOL");
    else if (view === "flg") list = list.filter((s) => ["FAIR LIQUIDITY GAP", "FLG SCALP", "FLG RECOVERY"].includes(s.strategy_label));
    if (asset !== "all") list = list.filter((s) => s.asset_class === asset);
    const p = PERIODS.find((x) => x.id === period);
    if (p && p.days) {
      const cutoff = Date.now() - p.days * 86400000;
      list = list.filter((s) => s.created_at && new Date(s.created_at).getTime() >= cutoff);
    }
    return list;
  }, [signals, view, asset, period]);

  const counts = useMemo(() => ({
    all: byStrategy.length,
    active: byStrategy.filter((s) => s.status === "active" && !isPending(s)).length,
    breakeven: byStrategy.filter((s) => s.status === "active" && !isPending(s) && s.breakeven).length,
    pending: byStrategy.filter(isPending).length,
    tp_hit: byStrategy.filter((s) => s.status === "tp_hit").length,
    sl_hit: byStrategy.filter((s) => s.status === "sl_hit").length,
    cancelled: byStrategy.filter((s) => s.status === "cancelled").length,
  }), [byStrategy]);

  const visible = useMemo(() => {
    if (status === "all") return byStrategy;
    if (status === "pending") return byStrategy.filter(isPending);
    if (status === "breakeven") return byStrategy.filter((s) => s.status === "active" && !isPending(s) && s.breakeven);
    if (status === "active") return byStrategy.filter((s) => s.status === "active" && !isPending(s));
    return byStrategy.filter((s) => s.status === status);
  }, [byStrategy, status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getSubscriptionState().then(setPushOn); }, []);
  useEffect(() => { api.get("/strategy-insights").then((r) => setInsights(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/lottery").then((r) => setLottery(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const t = setInterval(() => load(true), 25000);
    return () => clearInterval(t);
  }, [load]);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushOn) { await unsubscribePush(); setPushOn(false); toast.success("Notifiche push disattivate"); }
      else { await subscribePush(); setPushOn(true); toast.success("Notifiche push attivate!"); }
    } catch (e) { toast.error(e.message); }
    finally { setPushBusy(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">AI Trading Advisor</h1>
          <p className="text-sm text-zinc-400 mt-1">Aggiornati in tempo reale · XAUUSD · Bitcoin · Crypto · Forex</p>
        </div>
        <div className="flex items-center gap-3">
          {pushSupported() && (
            <button data-testid="push-toggle-btn" onClick={togglePush} disabled={pushBusy}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors disabled:opacity-50 ${
                pushOn ? "bg-blue-600 text-white hover:bg-blue-500" : "border border-white/20 text-zinc-300 hover:bg-white/5"}`}>
              {pushOn ? <BellRing size={16} /> : <BellOff size={16} />}
              {pushOn ? "Push attive" : "Attiva notifiche"}
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-8 border-b border-white/10" data-testid="dashboard-top-tabs">
        {[{ id: "segnali", label: "Segnali" }, { id: "mercati", label: "Mercati" }].map((t) => (
          <button key={t.id} data-testid={`top-tab-${t.id}`} onClick={() => setTopTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              topTab === t.id ? "border-amber-400 text-white" : "border-transparent text-zinc-400 hover:text-white"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {topTab === "mercati" ? (
        <div className="-mx-6 -mt-2" data-testid="dashboard-markets">
          <Markets />
        </div>
      ) : (
      <>
      {!user.is_premium && user.role !== "admin" && (
        <Link to="/pricing" data-testid="premium-banner"
          className="flex items-center gap-3 mb-8 p-4 rounded-md bg-amber-500/10 border border-amber-500/30 hover:bg-amber-500/15 transition-colors">
          <Crown size={20} className="text-amber-400 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-medium text-amber-400">Passa a Premium</div>
            <div className="text-xs text-zinc-400">Sblocca tutti i segnali premium e le analisi complete.</div>
          </div>
          <span className="text-sm font-medium text-amber-400">Scopri →</span>
        </Link>
      )}

      <Link to="/lottery" data-testid="dashboard-lottery-banner"
        className="flex flex-wrap items-center gap-3 mb-8 p-4 rounded-md bg-amber-500/[0.07] border border-amber-500/25 hover:border-amber-500/45 transition-colors">
        <Ticket size={20} className="text-amber-400 shrink-0" />
        <div className="flex-1 min-w-[180px]">
          <div className="text-sm font-medium text-amber-400">Lotteria · Estrazione premi</div>
          <div className="text-xs text-zinc-400">Il tuo numero fortunato: <span className="font-mono text-zinc-200">{user.lottery_number ?? "—"}</span></div>
        </div>
        <LotteryCountdown target={lottery?.next_draw_at} compact />
      </Link>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatBox testid="stat-total" label="Segnali Totali" value={stats.total} onClick={() => setClosedOpen(true)} />
          <StatBox testid="stat-active" label="Attivi" value={stats.active} color="text-blue-400" onClick={() => setActiveOpen(true)} />
          <StatBox testid="stat-tp" label="TP Raggiunti" value={stats.tp_hit} color="text-emerald-400" onClick={() => setTpOpen(true)} />
          <StatBox testid="stat-winrate" label="Win Rate" value={`${stats.win_rate}%`} color="text-amber-400" />
        </div>
      )}

      {stats && (
        <div className="mb-8">
          <h2 className="text-sm text-zinc-500 uppercase tracking-wider mb-3">Performance per strategia</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <WinRateDonut testid="donut-overall" label="Complessivo" value={stats.win_rate}
              tp={stats.tp_hit} sl={stats.sl_hit} closed={stats.closed} color="#10B981"
              pfs={insights?.overall_pf_signal} pfr={insights?.overall_pf_real}
              onClick={() => setInsightMode("overall")} />
            {(stats.by_strategy || []).map((s) => {
              const meta = STRAT_META[s.label] || { label: s.label, color: "#71717A", testid: "strategy" };
              const isFlg = s.label === "FAIR LIQUIDITY GAP";
              const insightKey = isFlg ? "FLG GROUP" : s.label;
              const mode = isFlg ? "FLG GROUP" : s.label;
              return (
                <WinRateDonut key={s.label}
                  testid={`donut-${meta.testid}`}
                  label={meta.label}
                  value={s.win_rate} tp={s.tp_hit} sl={s.sl_hit} closed={s.closed}
                  pfs={insights?.strategies?.[insightKey]?.pf_signal}
                  pfr={insights?.strategies?.[insightKey]?.pf_real}
                  color={meta.color}
                  onClick={() => setInsightMode(mode)} />
              );
            })}
          </div>
        </div>
      )}

      <TradeSearch />

      <div className="flex flex-wrap gap-2 mb-4">
        {VIEWS.map((v) => (
          <button key={v.id} data-testid={`view-${v.id}`} onClick={() => setView(v.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              view === v.id ? "bg-white text-black" : "bg-[#141414] border border-white/10 text-zinc-400 hover:text-white"}`}>
            {v.icon && <v.icon size={14} />}
            {v.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <span className="text-xs text-zinc-500 uppercase tracking-wider mr-1">Stato:</span>
        {STATUSES.map((st) => (
          <button key={st.id} data-testid={`status-${st.id}`} onClick={() => setStatus(st.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              status === st.id
                ? "bg-white/10 border-white/30 " + st.color
                : "bg-transparent border-white/10 text-zinc-500 hover:text-white"}`}>
            {st.label} <span className="font-mono opacity-70">({counts[st.id]})</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="text-xs text-zinc-500 uppercase tracking-wider mr-1">Categoria:</span>
        {ASSETS.map((a) => (
          <button key={a.id} data-testid={`asset-${a.id}`} onClick={() => setAsset(a.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              asset === a.id
                ? "bg-white/10 border-white/30 text-white"
                : "bg-transparent border-white/10 text-zinc-500 hover:text-white"}`}>
            {a.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <span className="text-xs text-zinc-500 uppercase tracking-wider mr-1">Periodo:</span>
        {PERIODS.map((p) => (
          <button key={p.id} data-testid={`period-${p.id}`} onClick={() => setPeriod(p.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              period === p.id
                ? "bg-white/10 border-white/30 text-white"
                : "bg-transparent border-white/10 text-zinc-500 hover:text-white"}`}>
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-20 text-zinc-500">Caricamento segnali...</div>
      ) : view === "messages" ? (
        messages.length === 0 ? (
          <div data-testid="no-messages" className="text-center py-20 text-zinc-500 border border-dashed border-white/10 rounded-md">
            Nessun messaggio al momento.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {messages.map((m) => (
              <MessageCard key={m.id} m={m} admin={user.role === "admin"} onDelete={deleteMessage} />
            ))}
          </div>
        )
      ) : view === "alerts" ? (
        alerts.length === 0 ? (
          <div data-testid="no-alerts" className="text-center py-20 text-zinc-500 border border-dashed border-white/10 rounded-md">
            Nessun avviso di mercato al momento.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {alerts.map((a) => (
              <MarketAlertCard key={a.id} a={a} admin={user.role === "admin"} onDelete={deleteAlert} />
            ))}
          </div>
        )
      ) : visible.length === 0 && !(view === "all" && status === "all" && (messages.length > 0 || biases.length > 0)) ? (
        <div data-testid="no-signals" className="text-center py-20 text-zinc-500 border border-dashed border-white/10 rounded-md">
          {view === "favorites"
            ? "Non hai ancora salvato segnali tra i preferiti. Tocca il cuore su un segnale per aggiungerlo."
            : "Nessun segnale per questo filtro al momento."}
        </div>
      ) : (
        <>
        <p data-testid="granularity-note" className="text-xs text-zinc-600 mb-4 flex items-start gap-1.5">
          <span className="text-amber-500/70">ⓘ</span>
          I trade sono verificati su candele 1h con Capital.com → possibile imprecisione di granularità su timeframe minori.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {view === "all" && status === "all"
            ? [
                ...biases.map((b) => ({
                  key: `bias-${b.id}`, ts: b.created_at,
                  el: <BiasCard b={b} admin={user.role === "admin"} onDelete={deleteBias} onOpen={setBiasSel} />,
                })),
                ...messages.map((m) => ({
                  key: `msg-${m.id}`, ts: m.created_at,
                  el: <MessageCard m={m} admin={user.role === "admin"} onDelete={deleteMessage} />,
                })),
                ...visible.map((s) => ({
                  key: `sig-${s.id}`, ts: s.created_at,
                  el: <SignalCard s={s} onToggleFavorite={toggleFavorite} />,
                })),
              ]
                .sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0))
                .map((x) => <React.Fragment key={x.key}>{x.el}</React.Fragment>)
            : visible.map((s) => <SignalCard key={s.id} s={s} onToggleFavorite={toggleFavorite} />)}
        </div>
        </>
      )}

      <StrategyInsightsModal
        open={!!insightMode}
        mode={insightMode}
        data={insights}
        onClose={() => setInsightMode(null)}
      />

      <ClosedSignalsModal open={closedOpen} onClose={() => setClosedOpen(false)} />
      <SignalsListModal variant="active" open={activeOpen} onClose={() => setActiveOpen(false)} />
      <SignalsListModal variant="tp" open={tpOpen} onClose={() => setTpOpen(false)} />
      <BiasSignalsModal bias={biasSel} open={!!biasSel} onClose={() => setBiasSel(null)} />
      </>
      )}
    </div>
  );
}
